import Collapse from "@kunukn/react-collapse";
import {Breadcrumb, Button, Descriptions, Form, message, Skeleton, Switch} from "antd";
import React, {useContext, useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {AiSettingsServiceContext, AppContextContext} from "../../Contexts";
import AiSettings, {OrganizationAiSettings} from "../../domain/AiSettings";
import AiCreditUsageIndicator from "../common/AiCreditUsageIndicator";
import * as globalStyles from "../App.module.css";
import * as styles from "./AiSettingsPage.module.css";
import {DocumentTitle} from "../DocumentTitle";
import AutorunSystemExtractorsInfo, {ORGANIZATION_SOURCE_AUTORUN_HELP} from "./AutorunSystemExtractorsInfo";

function AiSettingsOrganizationDetail() {
    const appContext = useContext(AppContextContext);
    const aiSettingsService = useContext(AiSettingsServiceContext);
    const {organizationId}: any = useParams();
    const [form] = Form.useForm();
    const [item, setItem] = useState<OrganizationAiSettings>();
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const title = item?.organizationName ?? "AI settings";
    const layout = {
        labelCol: {span: 8},
        wrapperCol: {span: 16},
    };
    const tailLayout = {
        wrapperCol: {offset: 8, span: 16},
    };

    useEffect(() => {
        reload();
    }, [aiSettingsService, form, organizationId]);

    if (!item) return <Skeleton/>;

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to="/ai-settings">AI settings</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>{item?.organizationName}</Breadcrumb.Item>
                </Breadcrumb>

                <h1>{title}</h1>

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button onClick={() => setEditMode(!editMode)}>Edit</Button>
                </div>

                {renderEditForm()}

                <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                    <Descriptions.Item label="Organization">
                        <Link to={`/organizations/${item?.organizationId}`}>{item?.organizationName}</Link>
                    </Descriptions.Item>
                    <Descriptions.Item label="Organization ID">{item?.organizationFriendlyId}</Descriptions.Item>
                    <Descriptions.Item label="AI feature">{renderAiFeature()}</Descriptions.Item>
                    <Descriptions.Item label="Add sys. attributes to org. sources">
                        {renderBoolean(item?.settings?.autoRunSystemAiExtractorsOnOrganizationSources)}
                    </Descriptions.Item>
                </Descriptions>
            </>
        </DocumentTitle>
    );

    function renderEditForm() {
        return (
            <Collapse isOpen={editMode}>
                <h3>Edit AI settings</h3>

                <Form
                    {...layout}
                    form={form}
                    className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]} ${styles["settings-form"]}`}
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="autoRunSystemAiExtractorsOnOrganizationSources"
                        label="Add sys. attributes to org. sources"
                        valuePropName="checked"
                        extra={ORGANIZATION_SOURCE_AUTORUN_HELP}
                    >
                        <Switch/>
                    </Form.Item>

                    <Form.Item {...tailLayout}>
                        <AutorunSystemExtractorsInfo
                            extractors={item?.settings?.autorunSystemExtractors}
                        />
                    </Form.Item>

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            Save
                        </Button>
                        <Button onClick={cancelEdit}>
                            Cancel
                        </Button>
                    </Form.Item>
                </Form>
            </Collapse>
        );
    }

    function renderAiFeature() {
        if (!item?.aiFeatureEnabled) {
            return <i>disabled</i>;
        }

        if (!item.aiTier) {
            return <i>enabled, no tier</i>;
        }

        return (
            <div className={styles["ai-feature-cell"]}>
                <Link to={`/definitions/ai-tiers/${item.aiTier.id}`}>{item.aiTier.name}</Link>
                <AiCreditUsageIndicator used={item.creditsUsedThisMonth ?? 0} limit={item.aiTier.maxCreditsPerMonth ?? 0}/>
            </div>
        );
    }

    function renderBoolean(value?: boolean) {
        return value ? "enabled" : "disabled";
    }

    function cancelEdit() {
        form.setFieldsValue(item?.settings);
        setEditMode(false);
    }

    function onFinish(values: AiSettings) {
        setSaving(true);

        aiSettingsService.updateOrganization(organizationId, values)
            .then(() => {
                message.success("AI settings updated.");
                setEditMode(false);
                reload();
            })
            .finally(() => setSaving(false));
    }

    function reload() {
        aiSettingsService.getOrganization(organizationId)
            .then(value => {
                setItem(value);
                form.setFieldsValue(value.settings);
            });
    }
}

export default AiSettingsOrganizationDetail;
